import { motion } from "framer-motion";
import { fadeInUp, fadeInRight, staggerContainer, viewportConfig } from "@/lib/motion";
import { Separator } from "@/components/ui/separator";
import { Zap, Shield, Settings, TrendingUp } from "lucide-react";

const ENGINEER_IMG = "https://images.unsplash.com/photo-1760553120312-2821bf54e767?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxyZW5ld2FibGUlMjBlbmVyZ3klMjBlbmdpbmVlciUyMHRhYmxldCUyMGZ1dHVyaXN0aWMlMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc3MTQ4NTkyOXww&ixlib=rb-4.1.0&q=85";

const METRICS = [
  { icon: Zap, value: "High Density", label: "Target Energy / kg" },
  { icon: Shield, value: "Multi-Layer", label: "Safety Architecture" },
  { icon: Settings, value: "In-House", label: "BMS Development" },
  { icon: TrendingUp, value: "Scalable", label: "Planned Production" },
];

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay overflow-hidden">
      <div className="section-glow absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Section Label */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mb-4"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // Who We Are
          </span>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 lg:gap-16 items-stretch">
          {/* Left Content */}
          <div className="md:col-span-7">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInUp}
              className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 mb-4 md:mb-6 leading-tight"
            >
              Engineering the backbone of{" "}
              <span className="text-[#1E96FC]">India's energy transition</span>
            </motion.h2>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInUp}
              className="space-y-4 text-slate-400 leading-relaxed"
            >
              <p>
                Sharda Energy Tech Pvt. Ltd. is a Sharda Group venture being
                built to engineer high-performance lithium-ion battery systems
                for electric mobility, grid storage, and industrial applications.
              </p>
              <p>
                Our vertically integrated infrastructure is designed to give us complete control — from the ground up:
              </p>
              <ul className="space-y-2 pl-4">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1E96FC] mt-2 flex-shrink-0" />
                  Cell-level assembly through pack-level engineering
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1E96FC] mt-2 flex-shrink-0" />
                  In-house Battery Management System (BMS) development
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1E96FC] mt-2 flex-shrink-0" />
                  Rigorous quality validation planned at every stage
                </li>
              </ul>
              <p>
                We won't just assemble batteries — we're building complete energy
                solutions, engineered and calibrated for real-world operating conditions.
              </p>
            </motion.div>

            <Separator className="my-8 bg-[#003771]/30" />

            {/* Metrics */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={staggerContainer}
              className="grid grid-cols-2 sm:grid-cols-4 gap-6"
            >
              {METRICS.map((m, i) => (
                <motion.div
                  key={i}
                  variants={fadeInUp}
                  className="flex flex-col gap-2"
                >
                  <m.icon className="w-5 h-5 text-[#1E96FC]" />
                  <span className="text-lg font-bold text-slate-200">{m.value}</span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider">{m.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right Image */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportConfig}
            variants={fadeInRight}
            className="md:col-span-5"
          >
            <div className="relative rounded-sm overflow-hidden h-full min-h-[350px]">
              <img
                src={ENGINEER_IMG}
                alt="Sharda Energy Tech Engineering"
                className="w-full h-full object-cover absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#040D1C] via-[#040D1C]/40 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="glass-card rounded-sm p-5">
                  <span className="text-xs md:text-sm font-bold text-[#1E96FC] uppercase tracking-[0.15em]">
                    A Sharda Group Enterprise
                  </span>
                  <p className="text-sm md:text-base text-slate-300 mt-2 leading-relaxed">
                    Part of a diversified industrial conglomerate with decades of engineering heritage.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
